import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types (서버 응답 형태) ---
export interface ProjectMemberUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role?: string;
  // CHG-133 Step 6: 표시 일관성
  displayColor?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface ProjectMember {
  id: string;
  userId: string;
  projectId: string;
  projectRole: string;
  user: ProjectMemberUser;
}

// CHG-041: 워크플로우 상태 정보
export interface WorkflowStatusInfo {
  statusId: string;
  name: string;
  color: string;
  category: 'TODO' | 'IN_PROGRESS' | 'DONE';
  sortOrder: number;
}

export interface ProjectWorkflow {
  workflowId: string;
  name: string;
  statuses: WorkflowStatusInfo[];
}

export interface ProjectDetail {
  id: string;
  name: string;
  description: string | null;
  projectKey: string;
  // CHG-149: 자동 조직 전환용 — schema의 Project.organizationId (CHG-060 nullable)
  organizationId: string | null;
  projectType: string;
  visibility: string;
  startDate: string | null;
  endDate: string | null;
  leaderId: string | null;
  designFiles: { filename: string; originalName: string; url: string }[] | null;
  figmaLinks: { name: string; url: string }[] | null;
  defaultMenu: string;
  chatEnabled: boolean;
  enabledMenus: string[] | null;
  issueCounter: number;
  createdAt: string;
  updatedAt: string;
  // CHG-132: 보관 상태 — null이면 활성, set이면 archived (readonly)
  archivedAt?: string | null;
  archivedBy?: string | null;
  archivedMode?: 'GRADUATE' | 'KEEP' | 'DELETE' | null;
  members: ProjectMember[];
  workflow: ProjectWorkflow;
  _count: { issues: number; requirements: number; specifications: number; testCases: number };
}

export interface CreateProjectData {
  name: string;
  projectKey: string;
  description?: string;
  projectType?: string;
  visibility?: string;
  startDate?: string;
  endDate?: string;
  leaderId?: string;
  figmaLinks?: { name: string; url: string }[];
  memberIds?: string[];
  designFiles?: File[];
  workflowId?: string;
}

// --- API calls ---
export type ProjectArchivedFilter = 'active' | 'archived' | 'all';

export async function fetchProjects(archived: ProjectArchivedFilter = 'active') {
  // CHG-132: 보관 필터 — 기본은 활성, 'archived'면 보관함, 'all'은 양쪽
  const res = await api.get<ApiResponse<ProjectDetail[]>>(`/projects?archived=${archived}`);
  return res.data.data!;
}

export async function fetchProject(id: string) {
  const res = await api.get<ApiResponse<ProjectDetail>>(`/projects/${id}`);
  return res.data.data!;
}

export async function createProject(data: CreateProjectData) {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('projectKey', data.projectKey);
  if (data.description) formData.append('description', data.description);
  if (data.projectType) formData.append('projectType', data.projectType);
  if (data.visibility) formData.append('visibility', data.visibility);
  if (data.startDate) formData.append('startDate', data.startDate);
  if (data.endDate) formData.append('endDate', data.endDate);
  if (data.leaderId) formData.append('leaderId', data.leaderId);
  if (data.figmaLinks) formData.append('figmaLinks', JSON.stringify(data.figmaLinks));
  if (data.memberIds) formData.append('memberIds', JSON.stringify(data.memberIds));
  if (data.workflowId) formData.append('workflowId', data.workflowId);
  if (data.designFiles) {
    data.designFiles.forEach((f) => formData.append('designFiles', f));
  }

  const res = await api.post<ApiResponse<ProjectDetail>>('/projects', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data!;
}

export async function updateProject(id: string, data: Partial<CreateProjectData>) {
  const res = await api.put<ApiResponse<ProjectDetail>>(`/projects/${id}`, data);
  return res.data.data!;
}

export async function deleteProject(id: string) {
  await api.delete(`/projects/${id}`);
}

// CHG-132: 프로젝트 보관
export type ArchiveMode = 'GRADUATE' | 'KEEP' | 'DELETE';

export interface ArchiveProjectResult {
  projectId: string;
  archivedAt: string;
  archivedMode: ArchiveMode;
  spaceTransition: {
    mode: ArchiveMode;
    teamSpaceId?: string;
    documentsDeleted?: number;
    note?: string;
  };
}

export async function archiveProject(
  id: string,
  payload: { mode: ArchiveMode; graduateSpaceName?: string },
): Promise<ArchiveProjectResult> {
  const res = await api.post<ApiResponse<ArchiveProjectResult>>(`/projects/${id}/archive`, payload);
  return res.data.data!;
}

export async function addProjectMember(projectId: string, userId: string, projectRole: string) {
  const res = await api.post<ApiResponse<ProjectDetail>>(`/projects/${projectId}/members`, { userId, projectRole });
  return res.data.data!;
}

export async function removeProjectMember(projectId: string, memberId: string) {
  await api.delete(`/projects/${projectId}/members/${memberId}`);
}

export async function updateMemberRole(projectId: string, memberId: string, projectRole: string) {
  const res = await api.put<ApiResponse<ProjectMember>>(`/projects/${projectId}/members/${memberId}`, { projectRole });
  return res.data.data!;
}

export async function checkProjectKey(key: string) {
  try {
    const projects = await fetchProjects();
    return !projects.some((p) => p.projectKey === key);
  } catch {
    return true;
  }
}

export async function searchUsers(query: string): Promise<ProjectMemberUser[]> {
  // 서버 응답 형태: { userId, email, name, profileImage } — ProjectMemberUser({id, avatarUrl})로 매핑
  type ServerResult = { userId: string; email: string; name: string; profileImage: string | null };
  const res = await api.get<ApiResponse<ServerResult[]>>(`/users/search?q=${encodeURIComponent(query)}`);
  return (res.data.data ?? []).map((u) => ({
    id: u.userId,
    email: u.email,
    name: u.name,
    avatarUrl: u.profileImage,
  }));
}

// ── CHG-080: 프로젝트 즐겨찾기 ──

export interface FavoriteProject {
  id: string;
  name: string;
  projectKey: string;
  projectType: string;
  favoriteId: string;
}

export async function fetchFavorites(): Promise<FavoriteProject[]> {
  const res = await api.get<ApiResponse<FavoriteProject[]>>('/projects/favorites');
  return res.data.data ?? [];
}

export async function toggleFavorite(projectId: string): Promise<{ isFavorite: boolean; projectId: string; favoriteId?: string }> {
  const res = await api.post<ApiResponse<{ isFavorite: boolean; projectId: string; favoriteId?: string }>>(`/projects/${projectId}/favorite`);
  return res.data.data!;
}
